import React, { useRef, useEffect, useState, useCallback } from 'react';
import classNames from 'classnames';

import styles from './index.module.css';

export interface DurationTabItem<T extends string | number = string | number> {
  label: string;
  value: T;
  icon?: React.ReactNode;
}

interface DurationTabsProps<T extends string | number = string | number> {
  tabs: DurationTabItem<T>[];
  activeValue: T;
  onChange: (value: T) => void;
  /** 拉品等 loading 场景：展示单条骨架，不可点 */
  skeleton?: boolean;
  bgColor?: string;
  activeBgColor?: string;
  itemWidth?: number | string;
}

function DurationTabs<T extends string | number = string | number>({
  tabs,
  activeValue,
  onChange,
  skeleton = false,
  bgColor,
  activeBgColor,
  itemWidth,
}: DurationTabsProps<T>) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({});

  const updateIndicator = useCallback(() => {
    const activeIndex = tabs.findIndex((tab) => tab.value === activeValue);
    const activeTab = tabRefs.current[activeIndex];
    const wrapper = wrapperRef.current;
    if (activeTab && wrapper) {
      const wrapperRect = wrapper.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();
      setIndicatorStyle({
        width: tabRect.width,
        transform: `translateX(${tabRect.left - wrapperRect.left - 4}px)`,
      });
    }
  }, [tabs, activeValue]);

  useEffect(() => {
    if (skeleton) return;
    updateIndicator();
  }, [updateIndicator, skeleton]);

  return (
    <div
      className={classNames(styles.tabsWrapper, {
        [styles.skeleton]: skeleton,
      })}
      ref={wrapperRef}
      style={bgColor ? { backgroundColor: bgColor } : undefined}
    >
      <div
        className={styles.indicator}
        style={{
          ...indicatorStyle,
          ...(activeBgColor ? { backgroundColor: activeBgColor } : undefined),
        }}
      />
      {tabs.map((tab, index) => (
        <div
          key={tab.value}
          ref={(el) => {
            tabRefs.current[index] = el;
          }}
          className={classNames(styles.tabItem, {
            [styles.tabItemActive]: tab.value === activeValue,
          })}
          style={itemWidth ? { width: itemWidth } : undefined}
          onClick={() => onChange(tab.value)}
        >
          {tab.icon && <span className={styles.tabIcon}>{tab.icon}</span>}
          <span className={styles.tabLabel}>{tab.label}</span>
        </div>
      ))}
    </div>
  );
}

export default React.memo(DurationTabs) as <T extends string | number = string | number>(
  props: DurationTabsProps<T>
) => React.ReactElement;
